import React, { useState, useCallback, memo, useImperativeHandle, forwardRef } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';

/**
 * IsolatedEditSection - A completely isolated text editing component
 *
 * This component manages its own state internally to prevent parent re-renders
 * during typing. Uses plain HTML textarea for maximum performance.
 */
const IsolatedEditSection = memo(forwardRef(({
  initialContent,
  onSave,
  onCancel,
  isSaving = false,
  minRows = 15,
  showButtons = true
}, ref) => {
  // Local state - changes here won't affect parent
  const [content, setContent] = useState(initialContent || '');

  // Handle typing - only updates local state
  const handleChange = useCallback((e) => {
    setContent(e.target.value);
  }, []);

  // Handle save - passes final content to parent
  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(content);
    }
  }, [content, onSave]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  // Expose save method to parent via ref (for external save button)
  useImperativeHandle(ref, () => ({
    save: handleSave,
    getContent: () => content
  }), [handleSave, content]);

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <textarea
        value={content}
        onChange={handleChange}
        autoFocus
        disabled={isSaving}
        rows={minRows}
        style={{
          width: '100%',
          padding: '14px',
          fontSize: '14px',
          fontFamily: 'inherit',
          lineHeight: 1.6,
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          resize: 'vertical',
          boxSizing: 'border-box',
          outline: 'none',
          backgroundColor: '#fff',
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#475969';
          e.target.style.borderWidth = '1px';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)';
          e.target.style.borderWidth = '1px';
        }}
      />
      {showButtons && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCancel}
            disabled={isSaving}
            startIcon={<CloseIcon />}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      )}
    </Box>
  );
}));

IsolatedEditSection.displayName = 'IsolatedEditSection';

IsolatedEditSection.propTypes = {
  /** Initial content to display in the editor */
  initialContent: PropTypes.string,
  /** Callback when user saves - receives the final content string */
  onSave: PropTypes.func.isRequired,
  /** Callback when user cancels editing */
  onCancel: PropTypes.func,
  /** Whether save is in progress */
  isSaving: PropTypes.bool,
  /** Minimum number of rows */
  minRows: PropTypes.number,
  /** Whether to show save/cancel buttons (default: true) */
  showButtons: PropTypes.bool,
};

export default IsolatedEditSection;
